import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_CONFIG from "../../lib/ApiConfig";
import { useAuth } from "../../hooks/useAuth";
import { formatCurrency } from "../../lib/utils";
import { LoaderCircle, BadgeCheck } from "lucide-react";
import OwnliScreenWrapper, { OwnliButton, OwnliCard } from "../ownli/OwnliScreenWrapper";
import track from "../../lib/analytics/events";

const RewardsClaim = ({ customer: propCustomer }) => {
  const navigate = useNavigate();
  const { getIdToken } = useAuth();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [imagesLoadedCount, setImagesLoadedCount] = useState(0);
  const [rewardDeliveryLink, setRewardDeliveryLink] = useState(null);
  const [claimLoader, setClaimLoader] = useState(true);
  const [canClaim, setCanClaim] = useState(false);

  const [summary, setSummary] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
  });

  const selectedProduct =
    products && products.length > 0 ? products[selectedIndex] : null;

  const disabledClaim = !canClaim || selectedIndex < 0;

  useEffect(() => {
    track.rewardsClaimViewed();
  }, []);

  useEffect(() => {
    const loadSummary = async () => {
      setLoading(true);
      try {
        const token = await getIdToken();
        const path = API_CONFIG.ENDPOINTS.USER.REWARDS_SUMMARY;
        const url = API_CONFIG.getInternalUrl(path);

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-user-token": token,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setSummary(
            data || {
              total: 0,
              confirmed: 0,
              pending: 0,
            }
          );
        }
      } catch (err) {
        console.error("Error loading rewards claim summary:", err);
      } finally {
        setLoading(false);
      }
    };

    const loadProducts = async () => {
      setLoading(true);
      try {
        const token = await getIdToken();
        const path = `api/rewards/products`;
        const url = API_CONFIG.getInternalUrl(path);

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-user-token": token,
          },
        });

        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length) {
            setProducts(data);
            //setSelectedIndex(0);
          } else {
            setProducts([]);
          }
        }
      } catch (err) {
        console.error("Error loading rewards claim products:", err);
      } finally {
        setLoading(false);
      }
    };
    const canClaimRewards = async () => {
      const path = API_CONFIG.ENDPOINTS.USER.CAN_CLAIM_REWARDS;
      const url = API_CONFIG.getInternalUrl(path);
      const token = await getIdToken();
      const responseCanClaim = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-user-token": token,
        },
      });
      if (responseCanClaim.status === 200) {
        setCanClaim(true);
      }
    };
    const loadPage = async () => {
      await loadSummary();
      await loadProducts();
      await canClaimRewards();
    };
    loadPage();
  }, [getIdToken]);

  const handleClaim = async () => {
    if (!selectedProduct) {
      return;
    }
    try {
      setLoading(true);
      track.rewardClaimInitiated({
        product_id: selectedProduct.id,
        product_name: selectedProduct.name,
      });
      const token = await getIdToken();
      const path = `api/rewards/claim/?product=${selectedProduct.id}`;
      const url = API_CONFIG.getInternalUrl(path);

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-user-token": token,
        },
      });

      if (res.ok) {
        let link = null;
        const text = await res.text();

        try {
          const parsed = JSON.parse(text);

          // Handle different possible API shapes
          if (parsed?.rewardDeliveryLink) {
            link = parsed.rewardDeliveryLink;
          } else if (parsed?.link) {
            link = parsed.link;
          } else if (typeof parsed === "string") {
            link = parsed;
          }
        } catch {
          // Not JSON → fallback to raw text
          link = text;
        }

        setRewardDeliveryLink(link || null);
        setDialogOpen(true);
        track.rewardClaimSucceeded({
          product_id: selectedProduct.id,
          has_delivery_link: Boolean(link),
        });
      } else {
        track.rewardClaimFailed({
          product_id: selectedProduct.id,
          status: res.status,
        });
      }
    } catch (err) {
      console.error("Error claiming rewards:", err);
      track.rewardClaimFailed({
        product_id: selectedProduct.id,
        error: err?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    track.rewardClaimDismissed({ via: "close" });
    setDialogOpen(false);
    navigate("/rewards");
  };

  const handleNotNow = () => {
    track.rewardClaimDismissed({ via: "not_now" });
    navigate("/rewards");
  };

  // Track image loading for product cards so we can show a spinner until cards are ready
  useEffect(() => {
    setImagesLoadedCount(0);
  }, [products]);

  const imageProductsCount = products.filter((p) => p.imageSrc).length;
  const allImagesLoaded =
    !imageProductsCount || imagesLoadedCount >= imageProductsCount;

  const handleImageLoad = () => {
    setImagesLoadedCount((count) => count + 1);
  };

  const amountLabel = formatCurrency(summary?.confirmed || 0, "");

  return (
    <OwnliScreenWrapper className="px-0">
      <div className="min-h-[calc(100vh-96px)] flex items-center justify-center">
        <div className="max-w-3xl w-full">
          <OwnliCard>
            <div className="flex flex-col items-center text-center gap-8">
              {loading ? (
                <div className="pt-4">
                  <div className="flex flex-col items-center gap-3">
                    <LoaderCircle className="w-10 h-10 text-crimsonGlory animate-spin" />
                  </div>
                </div>
              ) : (
                <div>Select a reward to continue.</div>
              )}

              {products && products.length > 0 && (
                <div className="w-full relative">
                  {!allImagesLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                      <LoaderCircle className="w-8 h-8 text-crimsonGlory animate-spin" />
                    </div>
                  )}

                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-4 transition-opacity ${
                      allImagesLoaded ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {products.map((product, index) => {
                      const isActive = index === selectedIndex;
                      return (
                        <button
                          key={product.productId || index}
                          type="button"
                          onClick={() => {
                            setSelectedIndex(index);
                            track.rewardProductSelected({
                              product_id: product.id,
                              product_name: product.name,
                              index,
                            });
                          }}
                          className={`relative w-full rounded-3xl border bg-white transition-all p-2 sm:p-4 flex items-stretch justify-center ${
                            isActive
                              ? "border-crimsonGlory ring-2 ring-crimsonGlory/30 shadow-lg opacity-100"
                              : "border-gray-200 opacity-80 hover:opacity-100 hover:border-crimsonGlory/60"
                          }`}
                        >
                          <div className="w-full rounded-3xl overflow-hidden flex items-center justify-center">
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={product.images[0].src}
                                onLoad={handleImageLoad}
                                alt={product.name || "Reward option"}
                                className="w-full h-40 md:h-52 object-contain"
                              />
                            ) : (
                              <span className="text-white text-sm md:text-base font-semibold px-4 py-8">
                                {product.name || "Reward option"}
                              </span>
                            )}
                          </div>
                          {isActive && (
                            <BadgeCheck className="absolute bottom-1 right-1 w-9 h-9 text-white fill-crimsonGlory" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="w-full space-y-4 pt-4 pb-2 flex flex-col items-center">
                <p className="text-base text-veryDarkGrey">
                  Claim <span className="font-semibold">{amountLabel}</span>
                  {selectedProduct && (
                    <span>
                      {" "}
                      with{" "}
                      <span className="font-semibold">
                        {selectedProduct.name}
                      </span>
                    </span>
                  )}
                </p>

                <OwnliButton
                  onClick={handleClaim}
                  disabled={disabledClaim}
                  className="max-w-xs w-full shadow-lg"
                >
                  {loading ? "Loading Products..." : "Claim now"}
                </OwnliButton>

                <button
                  type="button"
                  disabled={loading}
                  onClick={handleNotNow}
                  className="mt-1 text-base text-crimsonGlory uppercase underline underline-offset-2 disabled:text-gray-400"
                >
                  not now
                </button>
              </div>
            </div>
          </OwnliCard>
        </div>
      </div>

      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="max-w-sm w-full mx-4">
            <OwnliCard>
              <div className="text-center space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mt-2">
                  Rewards redeemed!
                </h2>

                {rewardDeliveryLink && (
                  <>
                    <p className="text-sm text-veryDarkGrey">
                      <a
                        target="_blank"
                        id="external_code_link"
                        href={rewardDeliveryLink}
                        rel="noopener noreferrer"
                        onClick={() => {
                          track.rewardDeliveryLinkClicked({
                            product_id: selectedProduct?.id,
                          });
                          handleCloseDialog();
                        }}
                        className="text-crimsonGlory font-semibold text-lg hover:text-crimsonGlory/80"
                      >
                        Click here
                      </a>
                    </p>
                    <span>to receive your claim code</span>
                  </>
                )}

                <p className="text-sm text-veryDarkGrey">
                  You'll also receive a confirmation
                  <br />
                  email from Ownli.
                </p>

                <p
                  onClick={handleCloseDialog}
                  className="cursor-pointer text-crimsonGlory hover:text-crimsonGlory/80 text-lg font-semibold mx-auto w-max"
                >
                  Close
                </p>
              </div>
            </OwnliCard>
          </div>
        </div>
      )}
    </OwnliScreenWrapper>
  );
};

export default RewardsClaim;
